import isEqual from "lodash/isEqual";
import { useMemo, useRef, useState } from "react";

import { Display, DisplayContainer, StyledArrowIcon, StyledMenu, StyledMenuItem } from "./styles";

type Option<T> = {
  text: string;
  value: T;
};

type Props<T> = {
  className?: string;
  onOptionSelect: (selectedOptionValue: T) => void;
  options: Option<T>[];
  placeholder?: string;
  selectedOptionValue?: T;
};

export const DropdownMenu = <T,>({
  className,
  onOptionSelect,
  options,
  placeholder,
  selectedOptionValue,
}: Props<T>) => {
  const anchorElement = useRef<HTMLSpanElement>(null);
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const handleMenuOptionChanged = (optionValue: T) => {
    setIsOpenMenu(false);
    onOptionSelect(optionValue);
  };

  const selectedOptionText = useMemo(() => {
    if (typeof selectedOptionValue === undefined) return;
    return options.find(option => isEqual(selectedOptionValue, option.value))?.text;
  }, [selectedOptionValue, options]);

  return (
    <>
      <DisplayContainer className={className}>
        <Display
          aria-expanded={isOpenMenu ? "true" : undefined}
          aria-haspopup="true"
          onClick={() => setIsOpenMenu(true)}
          ref={anchorElement}
        >
          {selectedOptionText ?? placeholder}
          <StyledArrowIcon $isExpand={isOpenMenu} />
        </Display>
      </DisplayContainer>
      {/* TODO: without toggle the menu will be reopened when the component is remounted */}
      {isOpenMenu && (
        <StyledMenu
          anchorEl={anchorElement.current}
          anchorOrigin={{
            horizontal: "left",
            vertical: "top",
          }}
          onClose={() => setIsOpenMenu(false)}
          open={isOpenMenu}
          transformOrigin={{
            horizontal: "left",
            vertical: "top",
          }}
        >
          {options.map((option, index) => (
            <StyledMenuItem
              $isSelected={isEqual(selectedOptionValue, option.value)}
              key={index}
              onClick={() => {
                handleMenuOptionChanged(option.value);
              }}
            >
              {option.text}
            </StyledMenuItem>
          ))}
        </StyledMenu>
      )}
    </>
  );
};

DropdownMenu.displayName = "DropdownMenu";
